import { useApolloClient, useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { useRealmApp } from "../RealmApp";
import { useCallback } from "react";

export default function useClockInMutations(storeId) {
  const app = useRealmApp();

  return {
    addClockIn: useAddClockIn(
      `owner_id=${app.currentUser.id}&store_id=${storeId}`
    ),
    updateClockIn: useUpdateClockIn(
      `owner_id=${app.currentUser.id}&store_id=${storeId}`
    ),
    deleteClockIn: useDeleteClockIn(
      `owner_id=${app.currentUser.id}&store_id=${storeId}`
    ),
    deleteManyClockIns: useDeleteManyClockIns(
      `owner_id=${app.currentUser.id}&store_id=${storeId}`
    ),
  };
}

const AddClockInMutation = gql`
  mutation AddClockIn($clockIn: ClockInInsertInput!) {
    addedClockIn: insertOneClockIn(data: $clockIn) {
      _id
      sessionId
      chainSessionId
      storeId
      userId
      type
      geolocation
      faceId
      notes
      updated
      clockDatetime
      updateDatetime
      creationDatetime
    }
  }
`;

const UpdateClockInMutation = gql`
  mutation UpdateClockIn(
    $clockInId: ObjectId!
    $ownerId: String!
    $updates: ClockInUpdateInput!
  ) {
    updatedClockIn: updateOneClockIn(
      query: { _id: $clockInId, owner_id: $ownerId }
      set: $updates
    ) {
      _id
      sessionId
      chainSessionId
      storeId
      userId
      type
      geolocation
      faceId
      notes
      updated
      clockDatetime
      updateDatetime
      creationDatetime
    }
  }
`;

const DeleteClockInMutation = gql`
  mutation DeleteClockIn($clockInId: ObjectId!, $ownerId: String!) {
    deletedClockIn: deleteOneClockIn(
      query: { _id: $clockInId, owner_id: $ownerId }
    ) {
      _id
    }
  }
`;

const DeleteManyClockInsMutation = gql`
  mutation DeleteManyClockIns($clockInIds: [ObjectId!], $ownerId: String!) {
    deletedClockIn: deleteOneClockIn(
      query: { _id_in: $clockInIds, owner_id: $ownerId }
    ) {
      _id
    }
  }
`;

// const ClockInFieldsFragment = gql`
//   fragment ClockInFields on ClockIn {
//     _id
//     sessionId
//     chainSessionId
//     storeId
//     userId
//     type
//     geolocation
//     faceId
//     notes
//     updated
//     clockDatetime
//     updateDatetime
//     creationDatetime
//   }
// `;

function useAddClockIn(ownerId) {
  const [addClockInMutation] = useMutation(AddClockInMutation);

  const client = useApolloClient();
  return useCallback(
    async (clockIn) => {
      const { data } = await addClockInMutation({
        variables: {
          clockIn: {
            owner_id: ownerId,
            ...clockIn,
          },
        },
      });
      const { addedClockIn } = data;
      client.cache.evict({
        query: "GetClockInDate",
      });
      return addedClockIn;
    },
    [client, addClockInMutation]
  );
}

function useUpdateClockIn(ownerId) {
  const [updateClockInMutation] = useMutation(UpdateClockInMutation);
  const client = useApolloClient();
  //
  return useCallback(
    async (clockIn, updates) => {
      const { data } = await updateClockInMutation({
        variables: { clockInId: clockIn._id, ownerId, updates },
      });
      const { updatedClockIn } = data;
      return updatedClockIn;
    },
    [client, updateClockInMutation]
  );
}

function useDeleteClockIn(ownerId) {
  const [deleteClockInMutation] = useMutation(DeleteClockInMutation, {
    update: (cache, { data: { deletedClockIn } }) => {
      cache.modify({
        fields: {
          clockIns: (existingClockIns = [], { readField }) =>
            existingClockIns.filter(
              (ref) => deletedClockIn._id !== readField("_id", ref)
            ),
        },
      });
    },
  });
  return async (clockIn) => {
    const {
      data: { deletedClockIn },
    } = await deleteClockInMutation({
      variables: { clockInId: clockIn._id, ownerId },
    });
    return deletedClockIn;
  };
}

function useDeleteManyClockIns(ownerId) {
  const [deleteManyClockInsMutation] = useMutation(DeleteManyClockInsMutation, {
    update: (cache, { data: { deletedClockIn } }) => {
      cache.modify({
        fields: {
          clockIns: (existingClockIns = [], { readField }) =>
            existingClockIns.filter(
              (ref) => deletedClockIn._id !== readField("_id", ref)
            ),
        },
      });
    },
  });
  return async (clockInIds) => {
    const {
      data: { deletedClockIn },
    } = await deleteManyClockInsMutation({
      variables: { clockInIds, ownerId },
    });
    return deletedClockIn;
  };
}
